import React from "react";
import { Link } from "react-router-dom";
import { Github, Twitter } from "lucide-react";
import LumiereIcon from "@/components/LumiereIcon";
import { useLanguage } from "@/i18n";

const Footer = () => {
  const currentYear = new Date().getFullYear();
  const { t } = useLanguage();

  const footerLinks = {
    product: [
      { label: t('nav.features'), href: "#features" },
      { label: t('nav.providers'), href: "#providers" },
      { label: t('nav.themes'), href: "#themes" },
      { label: t('nav.privacy'), href: "#privacy" }
    ],
    resources: [
      { label: t('nav.github'), href: "https://github.com/ajamaica/lumiere", external: true },
      { label: "App Store", href: "https://apps.apple.com/app/lumiere-openclaw-ai-agents/id6758534255", external: true },
      { label: "Chrome Web Store", href: "https://chromewebstore.google.com/detail/lumiere/ppnnimejlhphcjckocpiinjmpmdfhhmg", external: true },
      { label: t('footer.documentation'), href: "https://github.com/ajamaica/lumiere#readme", external: true }
    ],
    legal: [
      { label: t('footer.mitLicense'), href: "https://github.com/ajamaica/lumiere/blob/main/LICENSE", external: true },
      { label: t('footer.privacyPolicy'), href: "/privacy-policy", internal: true }
    ]
  };

  return (
    <footer className="border-t border-border/50 bg-card/30">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12 sm:py-16">
        <div className="grid grid-cols-2 md:grid-cols-4 gap-8 lg:gap-12">
          {/* Brand */}
          <div className="col-span-2 md:col-span-1">
            <a href="#" className="flex items-center gap-2 mb-4">
              <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-primary to-accent flex items-center justify-center">
                <LumiereIcon className="w-5 h-5 text-primary-foreground" />
              </div>
              <span className="text-xl font-display font-bold text-foreground">
                Lumiere
              </span>
            </a>
            <p className="text-sm text-muted-foreground mb-4 max-w-xs">
              {t('footer.tagline')}
            </p>
            {/* Social links */}
            <div className="flex items-center gap-3">
              <a
                href="https://github.com/ajamaica/lumiere"
                target="_blank"
                rel="noopener noreferrer"
                className="w-9 h-9 rounded-lg bg-muted hover:bg-primary/10 flex items-center justify-center text-muted-foreground hover:text-primary transition-colors"
                aria-label="GitHub"
              >
                <Github className="w-4 h-4" />
              </a>
              <a
                href="https://twitter.com"
                target="_blank"
                rel="noopener noreferrer"
                className="w-9 h-9 rounded-lg bg-muted hover:bg-primary/10 flex items-center justify-center text-muted-foreground hover:text-primary transition-colors"
                aria-label="Twitter"
              >
                <Twitter className="w-4 h-4" />
              </a>
            </div>
          </div>

          {/* Product links */}
          <div>
            <h4 className="text-sm font-semibold text-foreground mb-4">{t('footer.product')}</h4>
            <ul className="space-y-3">
              {footerLinks.product.map((link) => (
                <li key={link.label}>
                  <a
                    href={link.href}
                    className="text-sm text-muted-foreground hover:text-foreground transition-colors"
                  >
                    {link.label}
                  </a>
                </li>
              ))}
            </ul>
          </div>

          {/* Resources links */}
          <div>
            <h4 className="text-sm font-semibold text-foreground mb-4">{t('footer.resources')}</h4>
            <ul className="space-y-3">
              {footerLinks.resources.map((link) => (
                <li key={link.label}>
                  <a
                    href={link.href}
                    target={link.external ? "_blank" : undefined}
                    rel={link.external ? "noopener noreferrer" : undefined}
                    className="text-sm text-muted-foreground hover:text-foreground transition-colors"
                  >
                    {link.label}
                  </a>
                </li>
              ))}
            </ul>
          </div>

          {/* Legal links */}
          <div>
            <h4 className="text-sm font-semibold text-foreground mb-4">{t('footer.legal')}</h4>
            <ul className="space-y-3">
              {footerLinks.legal.map((link) => (
                <li key={link.label}>
                  {link.internal ? (
                    <Link
                      to={link.href}
                      className="text-sm text-muted-foreground hover:text-foreground transition-colors"
                    >
                      {link.label}
                    </Link>
                  ) : (
                    <a
                      href={link.href}
                      target={link.external ? "_blank" : undefined}
                      rel={link.external ? "noopener noreferrer" : undefined}
                      className="text-sm text-muted-foreground hover:text-foreground transition-colors"
                    >
                      {link.label}
                    </a>
                  )}
                </li>
              ))}
            </ul>
          </div>
        </div>

        {/* Bottom bar */}
        <div className="mt-12 pt-8 border-t border-border/50 flex flex-col sm:flex-row items-center justify-between gap-4">
          <p className="text-sm text-muted-foreground">
            © {currentYear} Lumiere. {t('footer.copyright')}
          </p>
          <p className="text-sm text-muted-foreground">
            {t('footer.madeWith')}{" "}
            <span className="text-red-500">♥</span>
            {" "}{t('footer.forCommunity')}
          </p>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
